import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Copy, Info } from 'lucide-react';
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription, DialogClose } from './dialog';
import { formatCouncilQualifiedReference } from '../../lib/councils';

interface EnhancedReferenceDisplayProps {
  referenceNo: string;
  council?: string;
  className?: string;
  onOpenDetails?: () => void;
}

const EnhancedReferenceDisplay: React.FC<EnhancedReferenceDisplayProps> = ({
  referenceNo,
  council,
  className = '',
  onOpenDetails
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPAN = referenceNo && referenceNo.endsWith('/PAN');
  const qualifiedReference = formatCouncilQualifiedReference(referenceNo, council);

  // Reference type explanations (non-exhaustive, common Scottish/UK planning codes)
  const REF_EXPLANATIONS: Record<string, { title: string; description: string } > = {
    FUL: { title: 'Full Planning Permission (FUL)', description: 'Application for full planning permission.' },
    HSE: { title: 'Householder Application (HSE)', description: 'Alterations or extensions to a dwellinghouse or flat.' },
    LBC: { title: 'Listed Building Consent (LBC)', description: 'Consent required for works affecting a listed building.' },
    ADV: { title: 'Advertisement Consent (ADV)', description: 'Consent for the display of advertisements/signage.' },
    TCA: { title: 'Trees in Conservation Area (TCA)', description: 'Notification of works to trees in a conservation area.' },
    TPO: { title: 'Tree Preservation Order (TPO)', description: 'Consent for works to trees protected by a TPO.' },
    PPP: { title: 'Planning Permission in Principle (PPP)', description: 'Permission in principle, with details to follow in later applications.' },
    MSC: { title: 'Matters Specified in Conditions (MSC)', description: 'Application to approve details required by a prior consent.' },
    NMA: { title: 'Non-Material Amendment (NMA)', description: 'Minor change that does not materially alter an approved scheme.' },
    S42: { title: 'Section 42 Application (S42)', description: 'Application to develop without complying with a condition.' },
    CLP: { title: 'Certificate of Lawfulness (Proposed) (CLP)', description: 'Certificate that proposed use or works would be lawful.' },
    CLE: { title: 'Certificate of Lawfulness (Existing) (CLE)', description: 'Certificate that an existing use or works are lawful.' },
  };

  const extractTypeCode = (ref?: string): string | null => {
    if (!ref) return null;
    const parts = ref.split('/');
    const last = parts[parts.length - 1] || '';
    // Some refs end with suffixes like LBC, FUL; normalise to A-Z only
    const code = last.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return code || null;
  };
  const refCode = extractTypeCode(referenceNo || '');

  const handleMouseEnter = () => setShowTooltip(true);
  const handleMouseLeave = () => setShowTooltip(false);
  const handleClick = () => setShowTooltip(!showTooltip);

  // Close tooltip when clicking outside (mobile support)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTooltip]);

  if (!isPAN) {
    const info = refCode ? REF_EXPLANATIONS[refCode] : undefined;

    const copyRef = async () => {
      try {
        await navigator.clipboard.writeText(qualifiedReference || '');
        window.dispatchEvent(
          new CustomEvent('cityscope-toast', {
            detail: { title: 'Reference copied', description: qualifiedReference, variant: 'success' }
          })
        );
      } catch (_) {
        window.dispatchEvent(
          new CustomEvent('cityscope-toast', {
            detail: { title: 'Copy failed', description: 'Please copy manually', variant: 'destructive' }
          })
        );
      }
    };

    return (
      <div className={`flex min-w-0 items-center gap-2 ${className}`}>
        <Dialog>
          <DialogTrigger asChild>
            <button
              className="inline-flex min-w-0 items-center gap-2"
              aria-label="Reference info"
              title={qualifiedReference || 'Reference unavailable'}
            >
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--c-text-secondary)]" />
              <h3 className="label min-w-0 truncate text-[var(--c-text-secondary)] text-xs sm:text-sm">
                REF: {qualifiedReference || 'N/A'}
              </h3>
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[var(--c-surface)] border border-[var(--c-border)]">
          <DialogTitle className="text-[var(--c-text-primary)]">Reference details</DialogTitle>
          <DialogDescription className="text-[var(--c-text-secondary)]">
            {qualifiedReference}
          </DialogDescription>
          <div className="mt-2 text-sm text-[var(--c-text-primary)]">
            <div className="mb-3 text-[var(--c-text-secondary)]">
              Council reference: <span className="font-mono text-[var(--c-text-primary)]">{referenceNo}</span>
            </div>
            {refCode ? (
              <>
                <div className="font-medium">Type: {refCode}</div>
                <div className="mt-1 text-[var(--c-text-secondary)]">
                  {info ? (
                    <>
                      <div className="font-medium text-[var(--c-text-primary)]">{info.title}</div>
                      <div>{info.description}</div>
                    </>
                  ) : (
                    <>This code indicates the application type. We don’t have a description for {refCode} yet.</>
                  )}
                </div>
              </>
            ) : (
              <div className="text-[var(--c-text-secondary)]">Could not detect a reference type code.</div>
            )}
          </div>
          <div className="mt-4">
            <div className="label text-[var(--c-text-secondary)] mb-1">Common codes</div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[var(--c-text-secondary)]">
              {Object.entries(REF_EXPLANATIONS).slice(0,8).map(([code, meta]) => (
                <li key={code}><span className="font-mono text-[var(--c-text-primary)]">{code}</span>: {meta.title.replace(/ \(.+\)$/,'')}</li>
              ))}
            </ul>
          </div>
          {onOpenDetails && (
            <div className="mt-4 flex gap-3">
              <DialogClose asChild>
                <button className="btn" onClick={onOpenDetails}>
                  Open details
                </button>
              </DialogClose>
            </div>
          )}
          </DialogContent>
        </Dialog>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); copyRef(); }}
          className="reference-copy-button inline-flex shrink-0 items-center gap-1 rounded-full border border-[var(--dock-border)] bg-[var(--dock-bg)] px-2 py-1 text-[10px] text-[var(--c-text-secondary)] transition-colors hover:border-[var(--c-accent)]/50 hover:text-[var(--c-accent)]"
          aria-label="Copy reference"
          title="Copy reference"
        >
          <Copy size={12} strokeWidth={1.5} aria-hidden="true" />
          <span className="reference-copy-label">Copy</span>
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative inline-flex ${className}`}>
      {/* PAN badge */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-label="Pre-Application Notice – tap for info"
        className="flex items-center space-x-1.5 px-2 sm:px-2.5 py-1 rounded-full bg-[var(--c-accent-muted)] text-[var(--c-accent)] font-mono text-[10px] sm:text-xs border border-[var(--c-accent)]/30 backdrop-blur-sm shadow-sm cursor-help active:scale-95 transition-transform"
      >
        <div className="w-1.5 h-1.5 rounded-full bg-[var(--c-accent)] animate-ping-slow" />
        <AlertCircle className="w-3 h-3 text-[var(--c-accent)]" />
        <span className="font-medium">PAN</span>
        <span className="text-[var(--c-text-secondary)] hidden sm:inline">
          {referenceNo.replace('/PAN', '')}
        </span>
      </div>

      {/* Tooltip */}
      <AnimatePresence>
        {showTooltip && (
                     <motion.div
             initial={{ opacity: 0, y: 15, scale: 0.9 }}
             animate={{ opacity: 1, y: 0, scale: 1 }}
             exit={{ opacity: 0, y: 15, scale: 0.9 }}
             className="absolute top-full left-0 min-w-[280px] max-w-[320px] z-[1000] mt-4"
             style={{ pointerEvents: 'none' }}
           >
                         <motion.div
               className="relative rounded-xl border overflow-hidden bg-[var(--c-surface)] border-[var(--c-accent)]/40 shadow-2xl backdrop-blur-xl"
               style={{
                 background: 'linear-gradient(135deg, var(--c-surface) 0%, rgba(211, 175, 55, 0.05) 100%)',
                 boxShadow: `
                   0 20px 40px -8px rgba(0, 0, 0, 0.8),
                   0 0 0 1px rgba(211, 175, 55, 0.3),
                   0 0 20px rgba(211, 175, 55, 0.2),
                   inset 0 1px 0 rgba(255, 255, 255, 0.1)
                 `,
                 backdropFilter: 'blur(20px) saturate(150%)'
               }}
               initial={{ rotateX: -10 }}
               animate={{ rotateX: 0 }}
               transition={{ duration: 0.3, ease: 'easeOut' }}
             >
              {/* Header */}
              <div className="p-4 pb-3 border-b border-[var(--c-border)]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[var(--c-accent)] border border-[var(--c-accent)]">
                    <Info className="w-4 h-4 text-[var(--c-bg)]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-base leading-tight text-[var(--c-accent)] font-['Space_Grotesk']">
                      Pre-Application Notice
                    </h4>
                    <span className="text-[10px] font-['DM_Mono'] text-[var(--c-accent)] uppercase tracking-wider">
                      Consultation Notice
                    </span>
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-[var(--c-text-primary)]">
                  This is a <span className="font-medium text-[var(--c-accent)]">Pre-Application Notice (PAN)</span> – a prior consultation on a forthcoming application.
                </p>
              </div>

              {/* Body */}
              <div className="p-4 text-xs text-[var(--c-text-secondary)] leading-relaxed">
                PANs allow developers to engage with the community before submitting their formal planning application.
              </div>
            </motion.div>

                         {/* Arrow */}
             <div className="absolute -top-2 left-8 w-4 h-4 rotate-45 border-l border-t border-[var(--c-border)] bg-[var(--c-surface)]" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EnhancedReferenceDisplay; 
